import axios, { AxiosError } from "axios";
import { Encryptor } from "@/js/app/services/Encryptor";
import { isDevelopmentMode, mergeObject } from "@/js/app/support/utils";

const ignoredMessages: Array<RegExp> = [
    /MetaMask/i,
    /Failed to execute 'showModal' on 'HTMLDialogElement': The dialog is already open as a non-modal dialog, and therefore cannot be opened as a modal dialog\./,
];

/**
 * Check if the error comes from an outdated/non-existent build asset file.
 * This happens when users have a cached HTML that references old app-*.js files.
 * Common error types: ReferenceError, TypeError, SyntaxError, etc.
 */
async function isOutdatedAssetError(err: Error): Promise<boolean> {
    const stack = err.stack ?? '';

    // Check if the error stack contains references to build assets
    const assetPattern = /\/build\/assets\/app-[a-zA-Z0-9]{8}\.js/;
    const match = stack.match(assetPattern);

    if (!match) {
        return false;
    }

    // Check if the referenced asset file actually exists
    try {
        const response = await fetch(match[0], { method: 'HEAD' });
        // If file exists (status 200-299), this is NOT an outdated asset error
        return !response.ok;
    } catch {
        // File doesn't exist or network error - this is an outdated asset error
        return true;
    }
}

/**
 * A watchdog for if anyone pass an invalid error
 */
export async function chooseIfShouldLogError(err: Error): Promise<boolean> {
    if (undefined === err.name) {
        return false;
    }

    // Ignore errors from outdated build assets (false positives)
    const isOutdated = await isOutdatedAssetError(err);
    if (isOutdated) {
        return false;
    }

    // Ignore MetaMask connection errors
    if (err.message) {


        for (let i = 0; i < ignoredMessages.length; i++) {
            if (ignoredMessages[i].test(err.message)) {
                return false;
            }
        }
    }

    // Ignore Axios connection aborted errors (timeout, cancelled requests)
    if (err instanceof AxiosError && 'ECONNABORTED' === err.code) {
        return false;
    }

    return true;
}

/**
 * Generate the error message content from any error.
 * This function allows to append some detail from a custom Error object.
 */
function generateErrorMessageFormat(err: Error): string {
    if (err instanceof AxiosError) {
        return JSON.stringify(mergeObject({
            name: err.name,
            message: err.message,
            stack: err.stack,
            response: err.response,
        }, err));
    } else if ("NetworkError" === err.name) {
        return JSON.stringify(mergeObject({
            name: err.name,
            message: err.message,
            stack: err.stack,
            status: (err as Error & { status?: number })?.status ?? 0,
            statusText: (err as Error & { statusText?: string })?.statusText ?? "Unknown",
            url: (err as Error & { url?: string })?.url ?? "Unknown",
        }, err));
    }

    return JSON.stringify(mergeObject({
        name: err.name,
        message: err.message,
        stack: err.stack,
    }, err));
}

/**
 * Log a given error to the server/backend.
 */
export async function logErrorIntoServer(err: Error): Promise<void> {
    const preMessage = generateErrorMessageFormat(err);
    // Log the error to the server
    try {
        const messageContent = await new Encryptor().encrypt(preMessage);

        axios.post('/api/log-exception', {
            error: JSON.stringify(messageContent),
        }).catch(err => {
            if (isDevelopmentMode()) {
                // eslint-disable-next-line no-console
                console.error("Cannot log to the API", err);
            } else {
                // eslint-disable-next-line no-console
                console.error("Cannot SEND the error to the server. Please contact an administrator");
            }
        });
    } catch (err) {
        if (isDevelopmentMode()) {
            // eslint-disable-next-line no-console
            console.error("Cannot encrypt message:", err);
        } else {
            // eslint-disable-next-line no-console
            console.error("Cannot SEND the error to the server. Please contact an administrator");
        }
    }
}

/**
 * Hide error in production. With vitejs, this means using "npm run build"
 */
export function transformErrorFromEnvironment(err: Error): Error {
    return isDevelopmentMode()
        ? err
        : new Error("An error has occured. Please contact an administrator");
}

/**
 * Handle any error: format the error, log it if needed, take care about app environment
 */
export async function handleErrorAndLog(userErr: Error): Promise<void> {
    const shouldLog = await chooseIfShouldLogError(userErr);
    if (true === shouldLog) {
        logErrorIntoServer(userErr);
    }
    const error = transformErrorFromEnvironment(userErr);
    // eslint-disable-next-line no-console
    console.error(error);
}
